import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../utils/streamApi';
import PasswordInput from '../components/PasswordInput';
import { ServerLogsSection, SupportTab as MobileAppLogsSection } from './AdminConfigPage';
import { SUPPORT_ACCESS_CHANGED, fmtUntil, turnSupportAccessOff, useSupportAccess } from '../utils/supportAccess';
import '../css/parvekshak.css';

// App Support (PRVSK-109). Everything for getting help in one place:
//   1. let Parvekshak support sign in, read-only, for 2 / 24 / 72 hours;
//   2. send this box's logs to support with one button (or download them);
//   3. mobile app logs — "Report an Error" from the Parvekshak app (was Admin
//      Config → Support); what it sends also goes into the bundle in 2;
//   4. server logs — read the box's journal here (also on Admin Config → Health).
// 1 and 2 need the customer's own password typed again.

const box = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 18, marginBottom: 16 };
const inp = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text)', fontSize: 13 };
const btn = { padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text)', fontSize: 13, cursor: 'pointer', fontWeight: 600 };
const btnPri = { ...btn, background: 'var(--primary)', borderColor: 'var(--primary)', color: '#fff' };
const muted = { color: 'var(--text-muted)', fontSize: 13 };
const errStyle = { color: '#b91c1c', fontSize: 13, marginTop: 8 };
const fmtWhen = ts => new Date(ts * 1000).toLocaleString([], { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const fmtSize = b => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const detail = async r => (await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`;

const CONSENT_TEXT =
  'I agree to send diagnostic information from this system to Parvekshak support so they can look into ' +
  'the problem I reported. It contains system and app logs, camera connection logs, hardware health and ' +
  'settings. Passwords, keys, tokens, phone numbers and e-mail addresses are removed before anything is ' +
  'sent, and it contains no images, video clips, recordings or face data. Parvekshak uses it only to ' +
  'resolve this issue and deletes it automatically after 30 days. I can ask support to delete it sooner ' +
  '(Digital Personal Data Protection Act, 2023).';

// ── 1. Support access ──────────────────────────────────────────────────────
function SupportAccessCard() {
  const [st, reload] = useSupportAccess();
  const [hours, setHours] = useState(2);
  const [cams, setCams] = useState(false);
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [granted, setGranted] = useState(null);     // {username, password, until} — shown once
  const [copied, setCopied] = useState(false);

  const allow = async () => {
    setBusy(true); setErr('');
    try {
      const r = await apiFetch('/support/access', { method: 'POST', json: { hours, password: pw, allow_cameras: cams } });
      if (!r.ok) throw new Error(await detail(r));
      setGranted(await r.json()); setPw(''); setCams(false);
      window.dispatchEvent(new Event(SUPPORT_ACCESS_CHANGED));
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const off = async () => {
    setBusy(true); setErr('');
    try { await turnSupportAccessOff(); setGranted(null); reload(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Username: ${granted.username}\nPassword: ${granted.password}`);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — the text is on screen */ }
  };

  if (!st) return <div style={box}><h2 style={{ marginTop: 0, fontSize: 18 }}>Support access</h2><p style={muted}>Loading…</p></div>;
  if (st.unsupported) return null;
  if (!st.available) {
    return (
      <div style={box}>
        <h2 style={{ marginTop: 0, fontSize: 18 }}>Support access</h2>
        <p style={muted}>Support access can't be used on this system because a user named "support" already exists.
          Rename or delete that user first.</p>
      </div>
    );
  }
  const logins = (st.history || []).filter(h => h.event === 'login');

  return (
    <div style={box}>
      <h2 style={{ marginTop: 0, fontSize: 18 }}>Support access</h2>
      <p style={muted}>
        Let Parvekshak support sign in to this system for a limited time. Support can read logs, health,
        detection logs and settings (with passwords hidden). Support can't change settings, add or delete
        users, or see recordings, alert images or known faces.
      </p>

      {granted && (
        <div style={{ border: '1px solid #f0d49b', background: '#fff8eb', color: '#7a4700', borderRadius: 8, padding: 14, margin: '12px 0' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Give these to Parvekshak support</div>
          <div style={{ fontFamily: 'monospace', fontSize: 15, lineHeight: 1.7 }}>
            Username: <b>{granted.username}</b><br />Password: <b>{granted.password}</b>
          </div>
          <div style={{ fontSize: 12, marginTop: 6 }}>
            This password is shown only now. It stops working at {fmtUntil(granted.until)}, or as soon as you turn access off.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button style={btn} onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button>
            <button style={btn} onClick={() => setGranted(null)}>Done</button>
          </div>
        </div>
      )}

      {st.active ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: '#047857' }}>
            ● On until {fmtUntil(st.until)}{st.allow_cameras ? ', including cameras' : ''}
          </span>
          {st.can_manage && <button style={{ ...btn, color: '#b91c1c' }} disabled={busy} onClick={off}>
            {busy ? 'Turning off…' : 'Turn off now'}</button>}
        </div>
      ) : st.can_manage ? (
        <div style={{ display: 'grid', gap: 10, maxWidth: 460 }}>
          <label style={{ fontSize: 13 }}>Allow support access for{' '}
            <select style={inp} value={hours} onChange={e => setHours(Number(e.target.value))}>
              <option value={2}>2 hours</option><option value={24}>24 hours</option><option value={72}>72 hours</option>
            </select>
          </label>
          <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={cams} onChange={e => setCams(e.target.checked)} />
            Also allow support to view cameras (live view only)
          </label>
          <label style={{ fontSize: 13 }}>Your password, to confirm
            <PasswordInput style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={pw}
              onChange={e => setPw(e.target.value)} autoComplete="current-password" />
          </label>
          <div><button style={btnPri} disabled={busy || !pw} onClick={allow}>{busy ? 'Turning on…' : 'Allow support access'}</button></div>
        </div>
      ) : (
        <p style={muted}>Support access is off. Ask your site admin to turn it on.</p>
      )}
      {err && <div style={errStyle}>{err}</div>}

      {st.can_manage && (st.history || []).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>History</div>
          {logins.length > 0 && (
            <div style={{ fontSize: 13, marginBottom: 6 }}>Support last accessed your system on {fmtWhen(logins[0].at)}.</div>
          )}
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--text-muted)' }}>
            {st.history.slice(0, 10).map((h, i) => (
              <li key={i}>{fmtWhen(h.at)} — {h.event === 'login' ? 'support signed in' : `access ${h.event}`}{h.by ? ` by ${h.by}` : ''}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── captcha widget (Cloudflare Turnstile or hCaptcha) ───────────────────────
const CAPTCHA_SCRIPTS = {
  turnstile: { src: 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit', global: 'turnstile' },
  hcaptcha:  { src: 'https://js.hcaptcha.com/1/api.js?render=explicit', global: 'hcaptcha' },
};

function Captcha({ provider, siteKey, onToken, resetKey }) {
  const el = useRef(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    const p = CAPTCHA_SCRIPTS[provider] || CAPTCHA_SCRIPTS.turnstile;
    let widget = null, cancelled = false;
    const render = () => {
      if (cancelled || !el.current || !window[p.global]) return;
      el.current.innerHTML = '';
      widget = window[p.global].render(el.current, {
        sitekey: siteKey,
        callback: t => onToken(t),
        'expired-callback': () => onToken(''),
        'error-callback': () => { onToken(''); setErr('The captcha could not load. Check the internet connection.'); },
      });
    };
    if (window[p.global]) render();
    else {
      let s = document.querySelector(`script[data-captcha="${provider}"]`);
      if (!s) {
        s = document.createElement('script');
        s.src = p.src; s.async = true; s.dataset.captcha = provider;
        s.onerror = () => setErr('The captcha could not load. Check the internet connection, or use Download instead.');
        document.head.appendChild(s);
      }
      s.addEventListener('load', render);
    }
    return () => {
      cancelled = true;
      try { if (widget != null) window[p.global]?.remove?.(widget); } catch { /* already gone */ }
    };
  }, [provider, siteKey, onToken, resetKey]);
  return <div><div ref={el} />{err && <div style={errStyle}>{err}</div>}</div>;
}

// ── 2. Share logs with support ─────────────────────────────────────────────
function ShareLogsCard() {
  const [opts, setOpts] = useState(null);
  const [hours, setHours] = useState(6);
  const [note, setNote] = useState('');
  const [consent, setConsent] = useState(false);
  const [pw, setPw] = useState('');
  const [token, setToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [job, setJob] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const loadOpts = useCallback(async () => {
    try {
      const r = await apiFetch('/support/log-share/options');
      if (r.ok) setOpts(await r.json());
    } catch { /* offline */ }
  }, []);
  useEffect(() => { loadOpts(); }, [loadOpts]);
  const onToken = useCallback(t => setToken(t), []);

  // Poll the job while it runs.
  useEffect(() => {
    if (!job || !['queued', 'collecting', 'uploading'].includes(job.state)) return;
    const t = setTimeout(async () => {
      try {
        const r = await apiFetch(`/support/log-share/${job.id}`);
        if (r.ok) {
          const d = await r.json();
          setJob(d);
          if (['done', 'ready', 'error'].includes(d.state)) {
            loadOpts(); setToken(''); setCaptchaKey(k => k + 1);
          }
        }
      } catch { /* keep polling */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [job, loadOpts]);

  const start = async mode => {
    setBusy(true); setErr('');
    try {
      const r = await apiFetch('/support/log-share', { method: 'POST',
        json: { hours, note, consent, password: pw, mode, captcha_token: mode === 'upload' ? token : null } });
      if (!r.ok) throw new Error(await detail(r));
      const d = await r.json();
      setJob({ id: d.job_id, state: 'queued', progress: 0, message: 'Starting', mode });
      setPw('');
    } catch (e) { setErr(e.message); setToken(''); setCaptchaKey(k => k + 1); }
    finally { setBusy(false); }
  };

  const download = async () => {
    setErr('');
    try {
      const r = await apiFetch(`/support/log-share/${job.id}/download`);
      if (!r.ok) throw new Error(await detail(r));
      const name = (r.headers.get('content-disposition') || '').match(/filename="?([^";]+)"?/)?.[1]
        || `parvekshak-logs-${job.reference}.tar.gz`;
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { setErr(e.message); }
  };

  if (!opts) return null;                      // not this user's to do, or an older box
  const running = job && ['queued', 'collecting', 'uploading'].includes(job.state);
  const waitMin = Math.ceil((opts.retry_after_s || 0) / 60);
  const canSend = opts.upload_configured && opts.captcha?.site_key;

  return (
    <div style={box}>
      <h2 style={{ marginTop: 0, fontSize: 18 }}>Share logs with support</h2>
      <p style={muted}>
        Sends this system's logs to Parvekshak support, with passwords and personal details removed.
        You get a reference ID to quote to support.
      </p>

      {!running && (
        <div style={{ display: 'grid', gap: 10, maxWidth: 560 }}>
          <label style={{ fontSize: 13 }}>Logs from the last{' '}
            <select style={inp} value={hours} onChange={e => setHours(Number(e.target.value))}>
              {(opts.hours || [1, 6, 24, 72]).map(h => <option key={h} value={h}>{h === 1 ? '1 hour' : `${h} hours`}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 13 }}>What went wrong? (optional)
            <textarea style={{ ...inp, width: '100%', boxSizing: 'border-box', minHeight: 70 }} maxLength={2000}
              value={note} onChange={e => setNote(e.target.value)}
              placeholder="e.g. the gate camera stopped sending alerts last night" />
          </label>
          <label style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start', lineHeight: 1.45 }}>
            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: 2 }} />
            <span>{CONSENT_TEXT}</span>
          </label>
          <label style={{ fontSize: 13 }}>Your password, to confirm
            <PasswordInput style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={pw}
              onChange={e => setPw(e.target.value)} autoComplete="current-password" />
          </label>
          {canSend
            ? <Captcha provider={opts.captcha.provider} siteKey={opts.captcha.site_key} onToken={onToken} resetKey={captchaKey} />
            : <div style={muted}>Sending to support isn't set up on this system — use Download and send the file to
                support by e-mail or WhatsApp.</div>}
          {waitMin > 0 && <div style={{ ...muted, color: '#7a4700' }}>Please wait {waitMin} minutes before sending again.</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canSend && (
              <button style={btnPri} disabled={busy || !consent || !pw || !token || waitMin > 0}
                onClick={() => start('upload')}>Send to support</button>
            )}
            <button style={btn} disabled={busy || !consent || !pw} onClick={() => start('download')}>
              Download bundle instead</button>
          </div>
        </div>
      )}

      {job && (
        <div style={{ marginTop: 14 }}>
          {running && <>
            <div style={{ fontSize: 13, marginBottom: 6 }}>{job.message}…</div>
            <div style={{ height: 8, background: 'var(--surface-alt)', borderRadius: 4, overflow: 'hidden', maxWidth: 460 }}>
              <div style={{ width: `${job.progress || 0}%`, height: '100%', background: 'var(--primary)', transition: 'width .4s' }} />
            </div>
          </>}
          {job.state === 'done' && (
            <div style={{ border: '1px solid #a7f3d0', background: '#ecfdf3', color: '#065f46', borderRadius: 8, padding: 14 }}>
              <div style={{ fontWeight: 700 }}>Sent to support ✓</div>
              <div style={{ marginTop: 4 }}>Your reference ID: <b style={{ fontFamily: 'monospace', fontSize: 16 }}>{job.reference}</b></div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Quote it when you talk to support.{job.truncated ? ' The logs were large, so only the most recent part was sent.' : ''}</div>
            </div>
          )}
          {job.state === 'ready' && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
              <div style={{ fontWeight: 700 }}>Bundle ready ({fmtSize(job.size || 0)})</div>
              <div style={{ fontSize: 12, margin: '4px 0 8px' }}>Reference {job.reference}. Send the file to Parvekshak support by e-mail or WhatsApp.</div>
              <button style={btnPri} onClick={download}>Download</button>
            </div>
          )}
          {job.state === 'error' && <div style={errStyle}>{job.message}</div>}
        </div>
      )}
      {err && <div style={errStyle}>{err}</div>}

      {(opts.history || []).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Sent before</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--text-muted)' }}>
            {opts.history.slice(0, 10).map((h, i) => (
              <li key={i}>{fmtWhen(h.at)} — <b>{h.reference}</b>, last {h.hours} h, {fmtSize(h.size || 0)}
                {h.mode === 'download' ? ' (downloaded)' : ''}{h.by ? `, by ${h.by}` : ''}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function SupportPage() {
  return (
    <div style={{ padding: 24, maxWidth: 820, margin: '0 auto', color: 'var(--text)' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24 }}>🛟 App Support</h1>
      <p style={{ ...muted, marginTop: 0 }}>Get help from Parvekshak support. Nothing is shared until you choose to.</p>
      <SupportAccessCard />
      <ShareLogsCard />
      <h2 style={{ fontSize: 18, margin: '24px 0 8px' }}>Mobile app logs</h2>
      <MobileAppLogsSection />
      <h2 style={{ fontSize: 18, margin: '24px 0 0' }}>Server logs</h2>
      <ServerLogsSection />
    </div>
  );
}
